import inRange from 'lodash/inRange'
import isInteger from 'lodash/isInteger'
import { Environment } from '../../environment/app-settings'
import { SessionStorage } from '../cookie-storage'
import { STORAGEKEYS } from '../keys'
import {DateTime} from "luxon";

/**
 * Funkcja dodająca odpowiednią odmianę słowa "miesiąc" w zależności od ilości miesięcy ("miesiąca", "miesięcy", "miesiące")
 * Funkcja bazuje na artykule: https://sjp.pwn.pl/poradnia/haslo/Formy-rzeczownika-miesiac-po-liczebnikach;20948.html
 * @param {Number} monthsAmount - liczba reprezentująca ilość miesięcy  
 * @returns {String}
 */
export const monthsAmountDisplayedNames = (monthsAmount: number): string => {
   
   const lastDigit = Number(String(monthsAmount).slice(-1))
   const numeralNames = {
      OPTION_A: 'miesiąc',
      OPTION_B: 'miesiące',
      OPTION_C: 'miesięcy'
   }

   if(isInteger(monthsAmount)){
      if(monthsAmount === 1){ //Opcja a) - jeśeli ilość miesięcy = 1 
         return `${monthsAmount} ${numeralNames.OPTION_A}`
      }
      else if([2, 3, 4].includes(monthsAmount)){ //Opcja b) - jeżeli ilość miesięcy = 2 lub 3 lub 4
         return `${monthsAmount} ${numeralNames.OPTION_B}` 
      }
      else if(inRange(monthsAmount, 5, 21)){ //Opcja c) - jeśli ilość miesięcy jest z zakresu od 5 do 21
         return `${monthsAmount} ${numeralNames.OPTION_C}`
      }
      else if([2, 3, 4].includes(lastDigit)){ //Opcja b) - jeśli ostatnia cyfra ilości miesięcy to 2 lub 3 lub 4 
         return `${monthsAmount} ${numeralNames.OPTION_B}`
      }
      else if([0, 1, 5, 6, 7, 8, 9].includes(lastDigit)){ //Opcja c) - jeśli ostatnia cyfra ilości miesięcy to 0, 1, 5, 6, 7, 8 lub 9 
         return `${monthsAmount} ${numeralNames.OPTION_C}`
      }

      else return `${monthsAmount}` //Żadne z powyższych warunków
   }
   
   else return `${monthsAmount}` // Liczba nie jest całkowita
}

/**
 * Funkcja która po podaniu numeru telefonu zwraca przygotowany string do wstawienia w atrybut `href` znacznika `<a />`.
 * Dzięki dodaniu 'tel:' po kliknięciu w taki odnośnik, na urządzeniach mobilnych zostanie otwarta aplikacja do wykonywania połączeń. 
 * @param phoneNumber - Numer telefonu
 * @returns 
 */
export const preparePhoneNumberToHref = (phoneNumber: string | undefined) => {
   if(phoneNumber?.length){
      let phoneNumberTemp = ''
      const phonePrefix = Environment.phoneNumberPrefix.trim()
      
      if(phoneNumber.startsWith(phonePrefix)){
         phoneNumberTemp = phoneNumber.replaceAll(/\s/g, '')
      }
      else {
         phoneNumberTemp = `${phonePrefix}${phoneNumber}`.replaceAll(/\s/g, '')
      }

      return `tel:${phoneNumberTemp}`.replaceAll(/\s/g, '')
   }

   else return ''
}


/**
 * Funkcja pomocnicza która opóźnia wykonanie danego kawałka kodu o określoną parametrem liczę minisekund. 
 * Realizuje ona mechanizm "czekania" na wykonanie dalszego kawałka kodu
 * 
 * @param time - czas w milisekundach na jaki wykonanie funkcji ma zostać opóźnione
 * @returns 
 */
export const sleep = (time: number) => new Promise(r => setTimeout(r, time));

/**
 * @param { paymentDatedate } - Data w dowolnym formacie tekstowym
 * Funkcja pomocnicza zamieniająca podaną datę z harmonogramu na format 01.01.2000
 */
export const dateToDisplayString = (date: string, dateFormat: string = 'dd.MM.yyyy') => {
   return DateTime.fromISO(new Date(date).toISOString()).toFormat(dateFormat)
}


export enum UserOsTypes {
   WINDOWS = 'WINDOWS',
   MAC = 'MAC',
   IOS = 'IOS',
   ANDROID = 'ANDROID',
   UNKNOWN = 'UNKNOWN',
}

export const mObywatelLinksBasedOnUserOS = {
   [UserOsTypes.WINDOWS]: 'https://play.google.com/store/apps/details?id=pl.nask.mobywatel',
   [UserOsTypes.MAC]: 'https://apps.apple.com/pl/app/mobywatel/id1339613469',
   [UserOsTypes.IOS]: 'https://apps.apple.com/pl/app/mobywatel/id1339613469',
   [UserOsTypes.ANDROID]: 'https://play.google.com/store/apps/details?id=pl.nask.mobywatel',
}


/**
 * Funkcja zwracająca odpowiedni link do google play bądź app store
 * bazując na systemie operacyjnym użytkownika
 *
 * @returns {String | undefined} 
*/
export const getAppLinkBasedOnOS = (): string | undefined => {
   const userAgent = navigator.userAgent
   
   if (/windows/i.test(userAgent)) { // Windows
       return mObywatelLinksBasedOnUserOS[UserOsTypes.WINDOWS]
   }
   else if (/macintosh|mac os x/i.test(userAgent)) { // MacOS
       return mObywatelLinksBasedOnUserOS[UserOsTypes.MAC]
   }
   else if (/iPad|iPhone|iPod/.test(userAgent) && !('MSStream' in window)) { // iOS
       return  mObywatelLinksBasedOnUserOS[UserOsTypes.IOS]
   }
   else if (/android/i.test(userAgent)) { // Android
       return  mObywatelLinksBasedOnUserOS[UserOsTypes.ANDROID]
   }

   return undefined
}


/**
 * Funkcja zwracająca minimalną kwotę pożyczki. Logika biznesowa:
 * - jeśli minimalna kwota pożyczki z Alphy jest WIĘKSZA niż minimalna kwota z Monogo, to zwracamy kwotę z Alphy
 * - jeśli mininalna kwota z Alphy jest MNIEJSZA niż minimalna kwota z Monogo, to zwracamy kwotę z Monogo
 * @param amount - Liczba którą chcemy zaokrąglić
 * @returns {Number} Zaokrąglona liczba
 */
export const getMinimumLoanAmount = (minimumLoanAmountFromAlpha: number): number =>{
  const sessionStorage = new SessionStorage()

  const minimumLoanAmountFromConfig = sessionStorage.get(STORAGEKEYS.SET_MINIMUM_LOAN_AMOUNT) ? Number(sessionStorage.get(STORAGEKEYS.SET_MINIMUM_LOAN_AMOUNT)) : Environment.minimumLoanAmount
  
   if(minimumLoanAmountFromAlpha >= minimumLoanAmountFromConfig){
      return minimumLoanAmountFromAlpha
   }
   else {
      return minimumLoanAmountFromConfig
   }
}

/**
 * Funkcja pomocnicza do wykonywania requestów z mechanizmem ponawiania próby w przypadku błędu.
 * @param requestFunction Funkcja do wywołania
 * @param retries Ilość powtórzeń w przypadku błędu
 * @param delay Opóźnienie pomiędzy kolejnymi próbami (w ms)
 * @returns 
 */
export const retryRequestOnError = async <T>(requestFunction: () => Promise<T>, retries: number = 2, delay: number = 1000): Promise<T> => {
   let attempts = 0

   while (attempts < retries) {
      try {
         return await requestFunction()
      } 
      catch (error) {
         attempts++;

         if (attempts >= retries) {
            throw error
         }

         await new Promise(res => setTimeout(res, delay))
      }
   }

   throw new Error('Nie udało się wykonać żądania po określonej liczbie prób. Sprawdź logi w celu uzyskania szczegółów.')
}